import { ThumbsDownIcon, ThumbsUpIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useMixpanel } from '@/hooks/useMixpanel';
import { getProjectFeedback, type StorageKey, setProjectFeedback } from '@/libs/storage';
import { cn } from '@/libs/utils/cn';

interface FeedbackPromptProps {
  /** The question text to display */
  text: string;
  /** The event name to track in Mixpanel */
  eventName: string;
  /** Project ID to include in the Mixpanel event */
  projectId: string;
  /** Storage key to persist feedback state */
  storageKey: StorageKey;
  /** Additional properties to include in the Mixpanel event */
  eventProperties?: Record<string, unknown>;
  /** Optional className for the container */
  className?: string;
}

export function FeedbackPrompt({
  text,
  eventName,
  projectId,
  storageKey,
  eventProperties,
  className,
}: FeedbackPromptProps) {
  const [submitted, setSubmitted] = useState<boolean | null>(null);
  const { trackAction } = useMixpanel();

  // When there is no project context (e.g. the map surface, which is
  // project-agnostic), `projectId` is empty. Persisting under the empty key would
  // collide across every location, so feedback is not persisted in that case —
  // it is tracked but not remembered. A non-empty id keeps the existing per-id
  // persistence behaviour for the project map.
  const hasProjectContext = projectId !== '';

  // Load existing feedback from storage when component mounts or projectId/storageKey changes
  useEffect(() => {
    if (!hasProjectContext) {
      setSubmitted(null);
      return;
    }
    const existingFeedback = getProjectFeedback(storageKey, projectId);
    setSubmitted(existingFeedback);
  }, [storageKey, projectId, hasProjectContext]);

  const handleFeedback = (isUseful: boolean) => {
    if (submitted !== null) return;

    setSubmitted(isUseful);
    if (hasProjectContext) {
      setProjectFeedback(storageKey, projectId, isUseful);
    }
    trackAction(eventName, {
      feedback: isUseful,
      // Omit project_id entirely when there is no project context so analytics
      // is not polluted with an empty id.
      ...(hasProjectContext ? { project_id: projectId } : {}),
      ...eventProperties,
    });
    toast.success('Thanks for your feedback!');
  };

  return (
    <div className={cn('flex items-center gap-2 text-sm', className)}>
      <span className="text-muted-foreground">{text}</span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => handleFeedback(true)}
          disabled={submitted !== null}
          className={cn(
            'rounded-md p-1.5 transition-colors',
            submitted === true
              ? 'bg-green-100 text-green-600 dark:bg-green-950 dark:text-green-400'
              : submitted === false
                ? 'text-muted-foreground/50'
                : 'text-muted-foreground hover:bg-green-100 hover:text-green-600 dark:hover:bg-green-950 dark:hover:text-green-400',
          )}
          aria-label="Yes, this was useful"
        >
          <ThumbsUpIcon className="size-4" />
        </button>
        <button
          type="button"
          onClick={() => handleFeedback(false)}
          disabled={submitted !== null}
          className={cn(
            'rounded-md p-1.5 transition-colors',
            submitted === false
              ? 'bg-red-100 text-red-600 dark:bg-red-950 dark:text-red-400'
              : submitted === true
                ? 'text-muted-foreground/50'
                : 'text-muted-foreground hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-950 dark:hover:text-red-400',
          )}
          aria-label="No, this was not useful"
        >
          <ThumbsDownIcon className="size-4" />
        </button>
      </div>
    </div>
  );
}
